// src/contexts/CreatorContext.js

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const CreatorContext = createContext();

export const CreatorProvider = ({ children }) => {
  const [creatorData, setCreatorData] = useState({
    creatorCode: null,
    creatorName: null,
    isLoading: false,
    isValid: false,
    error: null
  });

  // Détecter le code créateur dans l'URL au chargement
  useEffect(() => {
    const detectCreatorFromURL = async () => {
      // Vérifier les paramètres d'URL
      const urlParams = new URLSearchParams(window.location.search);
      const creatorParam = urlParams.get('creator') || urlParams.get('ref');
      
      if (creatorParam) {
        await validateCreator(creatorParam);
      } else {
        // Vérifier si un créateur est déjà stocké localement
        const storedCreator = localStorage.getItem('creatorCode');
        const storedName = localStorage.getItem('creatorName');
        
        if (storedCreator && storedName) {
          setCreatorData({
            creatorCode: storedCreator,
            creatorName: storedName,
            isLoading: false,
            isValid: true,
            error: null
          });
        }
      }
    };

    detectCreatorFromURL();
  }, []);

  // Valider un code créateur via l'API
  const validateCreator = useCallback(async (code) => {
    setCreatorData(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    try {
      const response = await api.get(`/creator/${code}`);
      
      // ✅ CORRECTION: Lire response.data.data au lieu de response.data.creator
      if (response.data.success && response.data.data) {
        const creator = response.data.data;
        const { ref_code, name, is_active } = creator;
        
        if (!is_active) {
          throw new Error('Ce créateur n\'est plus actif');
        }

        console.log('✅ Créateur validé:', name, ref_code);

        // Stocker dans localStorage
        localStorage.setItem('creatorCode', ref_code);
        localStorage.setItem('creatorName', name);

        setCreatorData({
          creatorCode: ref_code,
          creatorName: name,
          isLoading: false,
          isValid: true,
          error: null
        });

        return true;
      } else {
        throw new Error('Code créateur invalide');
      }
    } catch (error) {
      console.error('❌ Erreur validation créateur:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la validation du créateur';
      
      setCreatorData({
        creatorCode: null,
        creatorName: null,
        isLoading: false,
        isValid: false,
        error: errorMessage
      });

      // Nettoyer le localStorage
      localStorage.removeItem('creatorCode');
      localStorage.removeItem('creatorName');

      return false;
    }
  }, []);

  // Réinitialiser les données créateur
  const resetCreator = useCallback(() => {
    setCreatorData({
      creatorCode: null,
      creatorName: null,
      isLoading: false,
      isValid: false,
      error: null
    });

    // Nettoyer le localStorage
    localStorage.removeItem('creatorCode');
    localStorage.removeItem('creatorName');
  }, []);

  // Vérifier si l'utilisateur a une invitation valide
  const hasValidInvitation = useCallback(() => {
    return creatorData.isValid && creatorData.creatorCode !== null;
  }, [creatorData]);

  // Obtenir le message d'invitation
  const getInvitationMessage = useCallback(() => {
    if (hasValidInvitation()) {
      return `Invitation de ${creatorData.creatorName}`;
    }
    return 'Invitation requise pour ticket gratuit';
  }, [creatorData, hasValidInvitation]);

  const value = {
    creatorData,
    validateCreator,
    resetCreator,
    hasValidInvitation,
    getInvitationMessage,
    // Raccourcis pour accès rapide
    creatorCode: creatorData.creatorCode,
    creatorName: creatorData.creatorName,
    isValid: creatorData.isValid,
    isLoading: creatorData.isLoading,
    error: creatorData.error
  };

  return (
    <CreatorContext.Provider value={value}>
      {children}
    </CreatorContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useCreator = () => {
  const context = useContext(CreatorContext);
  if (!context) {
    throw new Error('useCreator doit être utilisé dans un CreatorProvider');
  }
  return context;
};

export default CreatorContext;